// data/signs.ts — typed access to the build-time sign index (FP6 Appendice v2).
//
// `sign-index.json` (compact, bundled) drives the searchable "Segnali" index and
// feeds the unified Appendice search. The full per-sign sheets live in
// `sign-details.json`, loaded LAZILY (dynamic import) only when a detail view
// opens — see `loadSignDetails()` — so the ~450 KB of prose stays out of the
// main chunk.

import signIndexJson from './sign-index.json';

export interface SignIndexEntry {
  id: string;
  name: string;
  figura: string | null;
  category: string | null;
  article: string | null;
  /** Relative image path, e.g. "img_sign_by_hash/HASH.png". */
  image: string | null;
  aliases: string[];
  /** ≤200-char meaning snippet for the list row. */
  meaning: string;
}

export interface SignDetail {
  shape: string | null;
  glyph: string | null;
  meaning: string | null;
  traps: string | null;
  /** Sign ids of confusion-pair lookalikes (same category), capped at 6. */
  related: string[];
}

export const SIGN_INDEX: SignIndexEntry[] = signIndexJson as SignIndexEntry[];

const SIGN_BY_ID = new Map(SIGN_INDEX.map((s) => [s.id, s]));

export function getSignById(id: string): SignIndexEntry | undefined {
  return SIGN_BY_ID.get(id);
}

/** Italian category → display label for the family filter chips. */
export const SIGN_CATEGORY_LABELS: Record<string, string> = {
  pericolo: 'Pericolo',
  divieto: 'Divieto',
  obbligo: 'Obbligo',
  precedenza: 'Precedenza',
  indicazione: 'Indicazione',
  orizzontale: 'Orizzontali',
  pannelli_integrativi: 'Pannelli',
  complementari: 'Complementari',
  luminosi: 'Luminosi',
  altro: 'Altro',
};

/** Distinct categories present in the index, in a stable display order. */
export function signCategories(): string[] {
  const order = Object.keys(SIGN_CATEGORY_LABELS);
  const present = new Set(SIGN_INDEX.map((s) => s.category).filter(Boolean) as string[]);
  return order.filter((c) => present.has(c));
}

/** Lazily loads the full per-sign detail sheets (dynamic import → own chunk). */
export async function loadSignDetails(): Promise<Record<string, SignDetail>> {
  const mod = await import('./sign-details.json');
  return (mod.default ?? mod) as Record<string, SignDetail>;
}
